import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import AhModal from './AhModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Ah {
  ah_id: number;
  ah_name: string;
  ah_status: number;
  ah_parent: number;
  parent_name: string;
}

const AccountHeads: React.FC = () => {
  const [ahs, setAhs] = useState<Ah[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Ah | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchAh = async () => {
    try {
      const data = await apiRequest<Ah[]>({
        method: 'get',
        url: 'account-heads',
      });
      setAhs(data);
    } catch (error) {
      showError('Failed to fetch Account Heads');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchAh();
  }, []);

  const handleEditClick = (record: Ah) => {
    setIsEditMode(true);
    setInitialValues({
      ah_id: record.ah_id,
      ah_name: record.ah_name,
      ah_status: record.ah_status,
      ah_parent: record.ah_parent,
      parent_name: record.parent_name,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchAh();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (ah_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-account-head/${ah_id}`,
      });
      showSuccess('Account Head deleted');
      fetchAh();
    } catch {
      showError('Failed to delete Account Head');
    }
  };

  const handleStatusToggle = async (qnId: number) => {
    setAhs((prev) =>
      prev.map((item) =>
        item.ah_id === qnId
          ? { ...item, ah_status: item.ah_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-account-head/${qnId}`,
      });
      showSuccess('Account Head status updated');
    } catch (error) {
      showError('Failed to update status');
      setAhs((prev) =>
        prev.map((item) =>
          item.ah_id === qnId
            ? { ...item, ah_status: item.ah_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Ah>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Ah> = [
    {
      title: 'Account Head',
      dataIndex: 'ah_name',
      key: 'ah_name',
      filters: Array.from(new Set(ahs.map((s) => s.ah_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.ah_name || null,
      onFilter: (value, record) => record.ah_name.includes(value as string),
      sorter: (a, b) => a.ah_name.localeCompare(b.ah_name),
      sortOrder: sortedInfo.columnKey === 'ah_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.ah_name}</div>,
    },
    {
      title: 'Parent',
      dataIndex: 'parent_name',
      key: 'parent_name',
      filters: Array.from(new Set(ahs.map((s) => s.parent_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.parent_name || null,
      onFilter: (value, record) => record.parent_name?.includes(value as string),
      sorter: (a, b) => a.parent_name?.localeCompare(b.parent_name || '') || 0,
      sortOrder: sortedInfo.columnKey === 'parent_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.parent_name || 'Root'}</div>,
    },
  ];



  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Ah) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.ah_status}
                onChange={() => handleStatusToggle(record.ah_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this Account Head?"
              onConfirm={() => handleDelete(record.ah_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Account Head</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Account Head
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Ah>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="ah_id"
          dataSource={ahs.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 15 }}
          locale={{ emptyText: 'No Account Head found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, ahs.length)} of{' '}
                {ahs.length}
              </span>
              <Pagination
                {...pagination}
                total={ahs.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <AhModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default AccountHeads;